import type React from "react";
import { useState } from "react";
import { Avatar, AvatarFallback } from "@planta-v2/pds-ui/avatar";
import { PlantaAvatarImage } from "@planta-v2/pds-ui/planta-avatar-image";
import { StringUtils } from "../../utils/string.utils";
import { cn } from "@planta-v2/pds-ui/lib/utils";
import { formatProfileSubtitle } from "@shared/utils/profile-subtitle.utils";
import { i18n } from "@frontend/i18n";
import { ProfilePhotoPreviewModal } from "./ProfilePhotoPreviewModal";

export interface ProfileHeaderProps {
  readonly displayName: string;

  readonly photoUrl: string | null;

  readonly headline: string | null;

  readonly company: string | null;

  readonly className?: string;
}

export function ProfileHeader({
  displayName,
  photoUrl,
  headline,
  company,
  className,
}: ProfileHeaderProps): React.JSX.Element {
  const [isPhotoPreviewOpen, setIsPhotoPreviewOpen] = useState(false);

  const subtitle = formatProfileSubtitle(
    { title: headline, companyName: company },
    ({ position, company: companyName }) =>
      i18n.t("shared.profile.subtitle.withCompany", {
        position,
        company: companyName,
      }),
  );

  const avatar = (
    <Avatar className="h-20 w-20 flex-shrink-0 rounded-full border border-foreground/10">
      {photoUrl !== null && (
        <PlantaAvatarImage src={photoUrl} alt={displayName} />
      )}
      <AvatarFallback className="bg-muted-foreground/20 font-serif text-xl font-normal text-foreground/75">
        {StringUtils.getInitials(displayName)}
      </AvatarFallback>
    </Avatar>
  );

  return (
    <>
      <div data-testid="profile-header" className={cn("flex gap-4", className)}>
        {photoUrl !== null ? (
          <button
            type="button"
            onClick={() => {
              setIsPhotoPreviewOpen(true);
            }}
            aria-label={i18n.t("participantProfile.photo.enlarge")}
            data-testid="profile-photo-button"
            className="cursor-pointer bg-transparent border-none p-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {avatar}
          </button>
        ) : (
          avatar
        )}

        <div className="flex flex-col justify-center gap-2 min-w-0">
          <div className="flex flex-col gap-1">
            <h1
              className="font-serif text-xl font-normal truncate text-foreground"
              data-testid="profile-display-name"
            >
              {displayName}
            </h1>

            {subtitle !== "" && (
              <p
                className="text-pds-2xs text-muted-foreground leading-tight"
                data-testid="profile-headline"
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
      {isPhotoPreviewOpen && photoUrl !== null && (
        <ProfilePhotoPreviewModal
          isOpen={isPhotoPreviewOpen}
          onClose={() => {
            setIsPhotoPreviewOpen(false);
          }}
          photoUrl={photoUrl}
          displayName={displayName}
        />
      )}
    </>
  );
}
